"use client";

/**
 * FillMissingStep — collects missing required fields + action extras.
 *
 * Inputs only show columns where the source row was null/empty (per backfill
 * policy in lib/actions/resolution/backfill.ts). Save persists the new value
 * back to the row going forward.
 */

import { useEffect, useRef, useState } from "react";

import {
  Button,
  Input,
  Label,
  ResponsiveCommandSelect,
  Textarea,
} from "@wtc/ui";

import type { FieldOption, FieldSpec } from "@/lib/actions/resolution/types";

import { useResolverContext } from "../ResolverContext";

interface Props {
  missingFields: FieldSpec[];
  extraInputs: FieldSpec[];
}

export function FillMissingStep({ missingFields, extraInputs }: Props) {
  const { setFields, setExtras } = useResolverContext();
  const [values, setValues] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const f of [...missingFields, ...extraInputs]) {
      init[f.name] = f.defaultValue ?? "";
    }
    return init;
  });

  const update = (name: string, value: string) => {
    setValues((v) => ({ ...v, [name]: value }));
  };

  // NOT a <form> — ResponsiveCommandSelect renders a <button> without an
  // explicit `type`, so default `type="submit"` bubbles to the nearest <form>
  // and accidentally advances the resolver step on click (skipping the picker
  // dropdown entirely). Use div + explicit `type="button"` everywhere.
  const handleContinue = () => {
    const fieldNames = new Set(missingFields.map((f) => f.name));
    const fieldPayload: Record<string, string> = {};
    const extraPayload: Record<string, string> = {};
    for (const [name, val] of Object.entries(values)) {
      if (fieldNames.has(name)) fieldPayload[name] = val;
      else extraPayload[name] = val;
    }
    if (Object.keys(fieldPayload).length > 0) setFields(fieldPayload);
    if (Object.keys(extraPayload).length > 0) setExtras(extraPayload);
  };

  const allRequired = [...missingFields, ...extraInputs]
    .filter((f) => f.required)
    .every((f) => Boolean(values[f.name]?.trim()));

  return (
    <div className="space-y-4">
      {missingFields.length > 0 && (
        <p className="text-xs text-muted-foreground">
          These will be saved to the contact for next time.
        </p>
      )}
      {[...missingFields, ...extraInputs].map((f) => (
        <FieldInput
          key={f.name}
          spec={f}
          value={values[f.name] ?? ""}
          onChange={(v) => update(f.name, v)}
        />
      ))}
      <div className="flex justify-end">
        <Button
          type="button"
          onClick={handleContinue}
          disabled={!allRequired}
        >
          Continue
        </Button>
      </div>
    </div>
  );
}

/**
 * Searchable select with server-side refinement.
 *
 * Initial render uses `spec.options` (pre-loaded server-side). Typing fires a
 * debounced POST to /api/actions/resolver/field-options to refresh results.
 * Falls back to current options on fetch error — no UX gap.
 */
function ResolverSearchableSelect({
  spec,
  value,
  onChange,
}: {
  spec: FieldSpec;
  value: string;
  onChange: (v: string) => void;
}) {
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState<FieldOption[]>(spec.options ?? []);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = search.trim();
    // Empty query → restore initial options (already top-N from server).
    if (trimmed.length === 0) {
      setOptions(spec.options ?? []);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch("/api/actions/resolver/field-options", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            source: spec.optionsSource,
            query: trimmed,
          }),
        });
        if (!res.ok) return;
        const body = (await res.json()) as { options?: FieldOption[] };
        setOptions(body.options ?? []);
      } catch (err) {
        console.error("[FillMissingStep] field-options refresh failed", err);
      }
    }, 200);
  }, [search, spec.options, spec.optionsSource]);

  return (
    <ResponsiveCommandSelect
      value={value}
      onValueChange={(next) => onChange(next)}
      options={options}
      placeholder={spec.placeholder ?? "Search…"}
      title={spec.label}
      searchValue={search}
      onSearchChange={setSearch}
    />
  );
}

function FieldInput({
  spec,
  value,
  onChange,
}: {
  spec: FieldSpec;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={`resolver-field-${spec.name}`}>
        {spec.label}
        {spec.required && <span className="text-destructive"> *</span>}
      </Label>
      {spec.kind === "textarea" ? (
        <Textarea
          id={`resolver-field-${spec.name}`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={spec.placeholder}
          rows={4}
        />
      ) : spec.kind === "select" ? (
        spec.searchable && spec.optionsSource ? (
          <ResolverSearchableSelect
            spec={spec}
            value={value}
            onChange={onChange}
          />
        ) : (
          <ResponsiveCommandSelect
            value={value}
            onValueChange={(next) => onChange(next)}
            options={spec.options ?? []}
            placeholder={spec.placeholder ?? "Select…"}
            title={spec.label}
          />
        )
      ) : (
        <Input
          id={`resolver-field-${spec.name}`}
          type={spec.kind === "email" ? "email" : "text"}
          inputMode={spec.kind === "phone" ? "tel" : undefined}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={spec.placeholder}
        />
      )}
    </div>
  );
}
